/**
 * ITAM Integration & Export Hub — /assets/itam
 * Bulk-import via CSV with automatic mapping, direct connectors to GLPI/Lansweeper/ManageEngine/ServiceNow,
 * export inventory to Excel + PDF, generate CNCS submission payload.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, Upload, FileSpreadsheet, Download, Loader2, CheckCircle2, AlertTriangle, Cloud, Server, Send, ShieldCheck } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NOOP = () => null; // to appease unused-import checks if any

async function downloadBlob(path, filename) {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${BACKEND_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function ItamImport() {
  const [tab, setTab] = useState('csv');
  return (
    <Layout title="ITAM & CNCS · Importação e Exportação">
      <div className="mb-4 flex items-center justify-between">
        <Link to="/assets" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-assets">
          <ArrowLeft className="h-4 w-4" /> Voltar aos Ativos
        </Link>
        <span className="text-[11px] text-slate-400">Compatível com CNCS · NIS2 (DL 125/2025) · Todos os campos requeridos suportados</span>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="csv" data-testid="tab-csv">CSV Import (universal)</TabsTrigger>
          <TabsTrigger value="connectors" data-testid="tab-connectors">Conectores ITAM</TabsTrigger>
          <TabsTrigger value="export" data-testid="tab-export">Exportação</TabsTrigger>
          <TabsTrigger value="cncs" data-testid="tab-cncs">CNCS</TabsTrigger>
        </TabsList>

        <TabsContent value="csv"><CsvImportPanel /></TabsContent>
        <TabsContent value="connectors"><ConnectorsPanel /></TabsContent>
        <TabsContent value="export"><ExportPanel /></TabsContent>
        <TabsContent value="cncs"><CncsPanel /></TabsContent>
      </Tabs>
      {NOOP()}
    </Layout>
  );
}

// ─── CSV Import with column-mapping override ─────────────────────────────
function CsvImportPanel() {
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaultScope, setDefaultScope] = useState('importante');
  const [defaultCrit, setDefaultCrit] = useState('media');
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const doPreview = async () => {
    if (!csv || csv.trim().length < 10) return toast.error('Cole o CSV primeiro.');
    setPreviewing(true);
    try {
      const { data } = await api.post('/itam/import/preview', { csv_content: csv });
      setPreview(data);
      setMapping({ ...data.auto_mapping });
      toast.success('CSV analisado.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao analisar CSV.');
    } finally {
      setPreviewing(false);
    }
  };

  const doImport = async () => {
    setImporting(true);
    setResult(null);
    try {
      const { data } = await api.post('/itam/import/csv', {
        csv_content: csv,
        column_mapping: mapping,
        default_nis2_scope: defaultScope,
        default_criticality: defaultCrit,
      });
      setResult(data);
      toast.success(`${data.created} criados · ${data.updated} atualizados`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao importar.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><FileSpreadsheet className="h-5 w-5 text-indigo-600" /> Importar CSV com mapeamento automático</CardTitle>
        <CardDescription>Suporta qualquer exportação CSV (GLPI, Lansweeper, ManageEngine, ServiceNow, Intune, Jamf, etc.). Detetamos as colunas e mapeamos automaticamente para o schema CNCS/NIS2. Corrija onde for necessário.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50" data-testid="csv-file-label">
            <Upload className="h-3.5 w-3.5" /> Escolher CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="csv-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => setCsv(ev.target.result);
                reader.readAsText(file);
              }}
            />
          </label>
          <Button size="sm" onClick={doPreview} disabled={previewing || !csv} data-testid="csv-preview-btn">
            {previewing ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />A analisar…</> : 'Analisar mapeamento'}
          </Button>
        </div>
        <Textarea
          rows={6}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          placeholder="Cole aqui o CSV exportado do seu ITAM ou selecione um ficheiro"
          className="font-mono text-xs"
          data-testid="csv-textarea"
        />

        {preview && (
          <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="csv-preview-panel">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between text-sm">
              <span><strong>{preview.headers.length}</strong> colunas detetadas · {preview.sample.length} linhas de exemplo</span>
              {preview.missing_required_fields?.length > 0 && (
                <span className="text-red-600 text-xs inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Faltam campos obrigatórios: {preview.missing_required_fields.join(', ')}</span>
              )}
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2">Coluna do CSV</th>
                  <th className="text-left px-4 py-2">Campo NIS2AI / CNCS</th>
                  <th className="text-left px-4 py-2">Exemplo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preview.headers.map((h) => (
                  <tr key={h} data-testid={`map-row-${h}`}>
                    <td className="px-4 py-2 font-mono text-slate-800">{h}</td>
                    <td className="px-4 py-2">
                      <Select
                        value={mapping[h] || '__none__'}
                        onValueChange={(v) => setMapping({ ...mapping, [h]: v === '__none__' ? null : v })}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`map-select-${h}`}>
                          <SelectValue placeholder="(ignorar)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">(ignorar)</SelectItem>
                          {preview.cncs_fields.map((f) => (
                            <SelectItem key={f.key} value={f.key}>
                              {f.label}{f.required ? ' *' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-2 text-slate-500">{preview.sample[0]?.[h] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs">Âmbito NIS2 (default se coluna não fornecida)</Label>
                <Select value={defaultScope} onValueChange={setDefaultScope}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid="default-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="essencial">Essencial</SelectItem>
                    <SelectItem value="importante">Importante</SelectItem>
                    <SelectItem value="fora_ambito">Fora de âmbito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Criticidade default</Label>
                <Select value={defaultCrit} onValueChange={setDefaultCrit}>
                  <SelectTrigger className="h-8 w-32 text-xs" data-testid="default-criticality"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['baixa', 'media', 'alta', 'critica'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={doImport} disabled={importing} className="ml-auto" data-testid="csv-import-btn">
                {importing ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />A importar…</> : 'Importar ativos'}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm space-y-1" data-testid="csv-result">
            <div className="flex flex-wrap items-center gap-4">
              <span><strong className="text-emerald-700 tabular-nums">{result.created}</strong> criados</span>
              <span><strong className="text-blue-700 tabular-nums">{result.updated}</strong> atualizados</span>
              <span><strong className="text-red-700 tabular-nums">{result.errors?.length || 0}</strong> erros</span>
            </div>
            {result.errors?.length > 0 && (
              <div className="max-h-32 overflow-y-auto text-xs space-y-1 mt-1">
                {result.errors.map((e, i) => <div key={i} className="text-red-700">Linha {e.row}: {e.reason}</div>)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Direct ITAM connectors ──────────────────────────────────────────────
function ConnectorsPanel() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <ConnectorCard
        name="GLPI"
        icon={<Server className="h-5 w-5 text-cyan-600" />}
        endpoint="/itam/connectors/glpi/import"
        description="GLPI (open-source ITAM). Requer App-Token + User-Token gerados no perfil de utilizador."
        fields={[
          { key: 'api_url', label: 'URL da API', placeholder: 'https://glpi.mycorp.pt/apirest.php' },
          { key: 'app_token', label: 'App-Token', type: 'password' },
          { key: 'user_token', label: 'User-Token', type: 'password' },
          { key: 'limit', label: 'Máx. ativos', type: 'number', defaultValue: 500 },
        ]}
        testid="conn-glpi"
      />
      <ConnectorCard
        name="Lansweeper"
        icon={<Cloud className="h-5 w-5 text-blue-600" />}
        endpoint="/itam/connectors/lansweeper/import"
        description="Lansweeper Cloud (GraphQL API). Requer Personal Access Token + Site ID."
        fields={[
          { key: 'api_url', label: 'GraphQL URL', defaultValue: 'https://api.lansweeper.com/api/v2/graphql' },
          { key: 'api_token', label: 'Access Token', type: 'password' },
          { key: 'site_id', label: 'Site ID' },
          { key: 'limit', label: 'Máx. ativos', type: 'number', defaultValue: 500 },
        ]}
        testid="conn-lansweeper"
      />
      <ConnectorCard
        name="ManageEngine"
        icon={<Server className="h-5 w-5 text-orange-600" />}
        endpoint="/itam/connectors/manageengine/import"
        description="ManageEngine ServiceDesk Plus / AssetExplorer. Requer authtoken pessoal."
        fields={[
          { key: 'api_url', label: 'URL da API', placeholder: 'https://sdp.mycorp.pt/api/v3' },
          { key: 'auth_token', label: 'Auth Token', type: 'password' },
          { key: 'limit', label: 'Máx. ativos', type: 'number', defaultValue: 500 },
        ]}
        testid="conn-manageengine"
      />
      <ConnectorCard
        name="ServiceNow"
        icon={<Cloud className="h-5 w-5 text-emerald-600" />}
        endpoint="/itam/connectors/servicenow/import"
        description="ServiceNow CMDB (Table API cmdb_ci). Requer user/password ou credenciais OAuth-lite."
        fields={[
          { key: 'instance_url', label: 'Instance URL', placeholder: 'https://mycorp.service-now.com' },
          { key: 'username', label: 'Username' },
          { key: 'password', label: 'Password', type: 'password' },
          { key: 'table', label: 'Tabela CMDB', defaultValue: 'cmdb_ci' },
          { key: 'limit', label: 'Máx. ativos', type: 'number', defaultValue: 500 },
        ]}
        testid="conn-servicenow"
      />
    </div>
  );
}

function ConnectorCard({ name, icon, endpoint, description, fields, testid }) {
  const initial = {};
  fields.forEach(f => { initial[f.key] = f.defaultValue ?? ''; });
  const [cfg, setCfg] = useState(initial);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setTesting(true);
    setResult(null);
    try {
      const payload = { ...cfg };
      // Ensure limit is a number
      if ('limit' in payload) payload.limit = Number(payload.limit) || 100;
      const { data } = await api.post(endpoint, payload);
      setResult(data);
      toast.success(`${name}: ${data.created} novos · ${data.updated} atualizados`);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Falha na comunicação com o conector.';
      toast.error(`${name}: ${detail}`);
      setResult({ error: detail });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">{icon} {name}</CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {fields.map(f => (
          <div key={f.key}>
            <Label className="text-xs">{f.label}</Label>
            <Input
              type={f.type || 'text'}
              value={cfg[f.key]}
              onChange={e => setCfg({ ...cfg, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              className="h-8 text-xs"
              data-testid={`${testid}-${f.key}`}
            />
          </div>
        ))}
        <Button size="sm" onClick={run} disabled={testing} className="w-full mt-2" data-testid={`${testid}-run`}>
          {testing ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />A sincronizar…</> : <>Sincronizar</>}
        </Button>
        {result && !result.error && (
          <div className="text-xs text-slate-600 mt-2 p-2 bg-emerald-50 rounded border border-emerald-100">
            <CheckCircle2 className="inline h-3 w-3 mr-1 text-emerald-600" />
            {result.total_fetched} obtidos · {result.created} novos · {result.updated} atualizados
          </div>
        )}
        {result?.error && (
          <div className="text-xs text-red-700 mt-2 p-2 bg-red-50 rounded border border-red-100">
            <AlertTriangle className="inline h-3 w-3 mr-1" /> {result.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Export panel ────────────────────────────────────────────────────────
function ExportPanel() {
  const [downloading, setDownloading] = useState(null);

  const runDownload = async (kind) => {
    setDownloading(kind);
    try {
      const path = kind === 'xlsx' ? '/api/itam/export/excel' : '/api/itam/export/pdf';
      const filename = kind === 'xlsx' ? `inventario_nis2_${new Date().toISOString().slice(0, 10)}.xlsx` : `inventario_nis2_${new Date().toISOString().slice(0, 10)}.pdf`;
      await downloadBlob(path, filename);
      toast.success(`Ficheiro ${kind.toUpperCase()} descarregado.`);
    } catch (err) {
      toast.error(err.message || 'Falha ao exportar.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card data-testid="export-excel-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-600" /> Excel (XLSX)</CardTitle>
          <CardDescription>Ficheiro Excel com todas as colunas do inventário CNCS-compatível. Ideal para análise, auditoria interna ou envio a consultores.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => runDownload('xlsx')} disabled={downloading === 'xlsx'} data-testid="export-excel-btn">
            {downloading === 'xlsx' ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A gerar…</> : <><Download className="h-4 w-4 mr-2" /> Descarregar Excel</>}
          </Button>
        </CardContent>
      </Card>
      <Card data-testid="export-pdf-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-red-600" /> PDF (completo)</CardTitle>
          <CardDescription>PDF landscape com uma linha por ativo, com o logotipo da empresa configurado nas Definições.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => runDownload('pdf')} disabled={downloading === 'pdf'} data-testid="export-pdf-btn">
            {downloading === 'pdf' ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A gerar…</> : <><Download className="h-4 w-4 mr-2" /> Descarregar PDF</>}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── CNCS submission panel ───────────────────────────────────────────────
function CncsPanel() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [endpointUrl, setEndpointUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  const generatePayload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/itam/cncs/payload');
      setPayload(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao gerar payload.');
    } finally {
      setLoading(false);
    }
  };

  const downloadPayload = () => {
    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cncs_inventario_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/itam/cncs/submit', {
        endpoint_url: endpointUrl || null,
        api_key: apiKey || null,
      });
      toast.success(`Submissão ${data.submission_id} · ${data.status}`);
      await loadSubmissions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha na submissão.');
    } finally {
      setSubmitting(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      const { data } = await api.get('/itam/cncs/submissions');
      setSubmissions(data.items || []);
    } catch { /* silent */ }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="h-5 w-5 text-blue-600" /> Submissão CNCS · NIS2</CardTitle>
          <CardDescription>
            Gere o payload no formato pré-esperado pela CNCS para o inventário de ativos NIS2.
            Enquanto a API oficial CNCS não estiver publicada, o botão abaixo mocka a submissão e regista o payload para consulta manual.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Button onClick={generatePayload} disabled={loading} variant="outline" data-testid="cncs-generate-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A gerar…</> : 'Gerar payload'}
            </Button>
            {payload && <Button onClick={downloadPayload} variant="outline" data-testid="cncs-download-btn"><Download className="h-4 w-4 mr-2" /> Download JSON</Button>}
          </div>

          {payload && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cncs-summary">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-xs text-slate-500 uppercase tracking-wide">Total ativos</div>
                <div className="text-2xl font-bold tabular-nums">{payload.totals?.assets}</div>
              </div>
              <div className="rounded-lg bg-red-50 border border-red-100 p-3">
                <div className="text-xs text-red-600 uppercase tracking-wide">Críticos</div>
                <div className="text-2xl font-bold tabular-nums text-red-800">{payload.totals?.by_criticality?.critica}</div>
              </div>
              <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                <div className="text-xs text-blue-600 uppercase tracking-wide">Essenciais</div>
                <div className="text-2xl font-bold tabular-nums text-blue-800">{payload.totals?.by_scope?.essencial}</div>
              </div>
            </div>
          )}

          <div className="border-t border-slate-200 pt-4 space-y-2">
            <div className="text-sm font-semibold">Submeter à CNCS (opcional)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">URL de submissão (quando disponível)</Label>
                <Input value={endpointUrl} onChange={e => setEndpointUrl(e.target.value)} placeholder="https://api.cncs.gov.pt/…" className="h-8 text-xs" data-testid="cncs-endpoint" />
              </div>
              <div>
                <Label className="text-xs">API Key (opcional)</Label>
                <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} className="h-8 text-xs" data-testid="cncs-apikey" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={submit} disabled={submitting} data-testid="cncs-submit-btn">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A submeter…</> : <><Send className="h-4 w-4 mr-2" /> Submeter</>}
              </Button>
              <Button onClick={loadSubmissions} variant="ghost" size="sm" data-testid="cncs-refresh-btn">Ver histórico</Button>
            </div>
            <p className="text-[11px] text-slate-500">
              Se não indicar URL, será registada uma submissão em estado <code>pending_official_api</code> com o payload guardado para envio manual.
            </p>
          </div>

          {submissions.length > 0 && (
            <div className="border-t border-slate-200 pt-4">
              <div className="text-sm font-semibold mb-2">Histórico</div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {submissions.map(s => (
                  <div key={s.submission_id} className="flex items-center gap-3 text-xs p-2 rounded hover:bg-slate-50" data-testid={`cncs-sub-${s.submission_id}`}>
                    <Badge variant="secondary" className={s.status === 'submitted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : s.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-100 text-slate-700 border-slate-200'}>
                      {s.status}
                    </Badge>
                    <span className="font-mono text-slate-400">{s.submission_id}</span>
                    <span className="ml-auto text-slate-500">{(s.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                    <span className="text-slate-600 font-semibold">{s.assets_count} ativos</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
